window.CartState = (function () {
  const MUTATION_RE = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:$|[?#])/;
  const STALE_AFTER = 1500;
  const FORCE_STALE_AFTER = 200;
  const SETTLE_TIMEOUT = 10000;
  const OBSERVED_MATCH_MS = 100;
  const OBSERVED_TTL = 30000;

  let inflight = null;
  let inflightEpoch = -1;
  let cached = null;
  let cachedAt = 0;
  let cachedEpoch = -1;
  let epoch = 0;
  let pendingMutations = 0;
  let settleWaiters = [];
  let wrappedMutations = [];

  function markStale() {
    epoch += 1;
    cached = null;
    cachedEpoch = -1;
  }

  function invalidate() {
    markStale();
    document.dispatchEvent(new CustomEvent('cart:invalidated', { detail: { epoch: epoch } }));
  }

  function version() {
    return epoch;
  }

  function clone(cart) {
    if (!cart) return cart;
    if (typeof structuredClone === 'function') {
      try {
        return structuredClone(cart);
      } catch (error) {
        /* fall through */
      }
    }
    return JSON.parse(JSON.stringify(cart));
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    return (input && input.url) || '';
  }

  function absoluteUrl(url) {
    try {
      return new URL(url, window.location.href).href;
    } catch (error) {
      return url;
    }
  }

  function beginMutation(url) {
    const now = performance.now();
    wrappedMutations = wrappedMutations.filter((entry) => now - entry.at < OBSERVED_TTL);
    wrappedMutations.push({ href: absoluteUrl(url), at: now });
    pendingMutations += 1;
    markStale();
  }

  function endMutation() {
    pendingMutations = Math.max(0, pendingMutations - 1);
    invalidate();
    if (pendingMutations === 0) {
      const waiters = settleWaiters;
      settleWaiters = [];
      waiters.forEach((wake) => wake());
    }
  }

  function claimWrappedMutation(entry) {
    const index = wrappedMutations.findIndex(
      (wrapped) => wrapped.href === entry.name && Math.abs(entry.startTime - wrapped.at) < OBSERVED_MATCH_MS
    );
    if (index === -1) return false;
    wrappedMutations.splice(index, 1);
    return true;
  }

  function whenSettled() {
    const deadline = performance.now() + SETTLE_TIMEOUT;
    return new Promise((resolve) => {
      function check() {
        const remaining = deadline - performance.now();
        if (pendingMutations === 0 || remaining <= 0) {
          resolve();
          return;
        }
        let woken = false;
        const timer = setTimeout(wake, remaining);
        function wake() {
          if (woken) return;
          woken = true;
          clearTimeout(timer);
          setTimeout(check, 0);
        }
        settleWaiters.push(wake);
      }
      check();
    });
  }

  if (typeof window.fetch === 'function') {
    const nativeFetch = window.fetch;
    window.fetch = function (input) {
      const url = urlOf(input);
      if (!MUTATION_RE.test(url)) {
        return nativeFetch.apply(this, arguments);
      }
      beginMutation(url);
      let request;
      try {
        request = nativeFetch.apply(this, arguments);
      } catch (error) {
        endMutation();
        throw error;
      }
      return Promise.resolve(request).then(
        (response) => {
          endMutation();
          return response;
        },
        (error) => {
          endMutation();
          throw error;
        }
      );
    };
  }

  if (window.XMLHttpRequest) {
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      const target = urlOf(url);
      this.__cartStateMutationUrl = MUTATION_RE.test(target) ? target : null;
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (!this.__cartStateMutationUrl) {
        return nativeSend.apply(this, arguments);
      }
      beginMutation(this.__cartStateMutationUrl);
      this.addEventListener('loadend', endMutation, { once: true });
      try {
        return nativeSend.apply(this, arguments);
      } catch (error) {
        this.removeEventListener('loadend', endMutation);
        endMutation();
        throw error;
      }
    };
  }

  if (window.PerformanceObserver) {
    try {
      new PerformanceObserver((list) => {
        const unseen = list
          .getEntries()
          .filter((entry) => MUTATION_RE.test(entry.name) && !claimWrappedMutation(entry));
        if (unseen.length) {
          invalidate();
        }
      }).observe({ entryTypes: ['resource'] });
    } catch (error) {
      /* no-op */
    }
  }

  document.addEventListener('eurus:cart:items-changed', markStale);
  document.addEventListener('cart:updated', markStale);
  document.addEventListener('cart:refresh', markStale);
  document.addEventListener('cart:sections:swapped', markStale);

  function isUsable(force) {
    if (!cached || cachedEpoch !== epoch) return false;
    return performance.now() - cachedAt < (force ? FORCE_STALE_AFTER : STALE_AFTER);
  }

  function request() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('cart.js responded ' + response.status);
      }
      return response.json();
    });
  }

  function read(force) {
    if (isUsable(force)) {
      return Promise.resolve(clone(cached));
    }
    if (inflight && inflightEpoch === epoch) {
      return inflight.then(clone);
    }

    const startedAt = epoch;
    const previous = inflight;
    inflightEpoch = startedAt;

    const pending = (previous ? previous.catch(() => {}) : Promise.resolve())
      .then(request)
      .then((cart) => {
        if (epoch === startedAt) {
          cached = cart;
          cachedEpoch = startedAt;
          cachedAt = performance.now();
        }
        return cart;
      })
      .finally(() => {
        if (inflight === pending) {
          inflight = null;
          inflightEpoch = -1;
        }
      });

    inflight = pending;
    return pending.then(clone);
  }

  function get(options) {
    const force = !!(options && options.force);
    if (pendingMutations > 0) {
      return whenSettled().then(() => read(force));
    }
    return read(force);
  }

  return { get, invalidate, version };
})();
